import { useEffect, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { DEMO_WHATSAPP_URL } from './webinarConfig';

// Every entry here renders publicly and feeds the FAQPage JSON-LD schema.
// Do not add an entry whose answer is an unresolved [TODO: verify ...] placeholder —
// two such items ("Which TradingView plan do I need?", "What does it cost?") are
// intentionally omitted until their answers are confirmed.
const FAQS = [
  {
    q: "What if I can't attend live?",
    a: 'We currently do not share webinar recordings publicly. Please register for the next available live session or book a 1-on-1 setup demo if you want direct help sooner.',
  },
  {
    q: 'Do I need to change my Pine Script code?',
    a: "No. Your strategy or indicator stays in TradingView as it is. You add the AlgoSys webhook URL and message format to the alert — that's the integration.",
  },
  {
    q: 'Does this work with indicator alerts, or only strategy alerts?',
    a: 'Both. Strategy order-fill alerts and plain indicator condition alerts can each drive execution through AlgoSys.',
  },
  {
    q: 'Which brokers are supported?',
    a: 'FYERS, Zerodha, and MasterTrust are fully supported today. Nuvama integration is currently in testing — join the waitlist through the registration form. More brokers are on the roadmap; tell us yours when you register.',
  },
  {
    q: 'Where does my money sit?',
    a: 'With your broker, always. AlgoSys connects to your existing broker account to place orders and show you funds, orderbook, positions, and holdings. It never holds client funds.',
  },
  {
    q: 'Can I step in manually?',
    a: 'Anytime. Pause the strategy to stop new entries, exit a specific position, or kill everything from the dashboard.',
  },
  {
    q: 'Will my stop loss always fill at my exact price?',
    a: 'No — and no honest platform will tell you otherwise. AlgoSys places and manages exit orders automatically, which removes hesitation and forgotten stops. Final fills happen at your broker and the exchange, so fast markets and gaps can still cause slippage on any stop, automated or manual.',
  },
  {
    q: 'Does AlgoSys guarantee profits?',
    a: 'No. AlgoSys is a technology platform. Whether a strategy makes money depends on the strategy and the market. What AlgoSys improves is execution speed, risk discipline, and visibility.',
  },
  {
    q: 'Can I automate options with TradingView signals?',
    a: 'Yes. A TradingView signal can drive index option and dynamic option strategy execution on supported segments, managed as a single position with your risk rules.',
  },
];

export default function FaqAccordion() {
  const [openIndex, setOpenIndex] = useState(0);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="space-y-3">
      {FAQS.map((item, i) => {
        const open = openIndex === i;
        return (
          <div key={item.q} className="card overflow-hidden">
            <button
              type="button"
              className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
              aria-expanded={open}
              onClick={() => setOpenIndex(open ? -1 : i)}
            >
              <span className="font-display text-sm font-bold text-txt-primary">{item.q}</span>
              <ChevronDown
                size={18}
                className={`flex-none text-amber transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
              />
            </button>
            {open && (
              <div className="px-5 pb-4 text-sm leading-relaxed text-txt-secondary">
                {item.q === "What if I can't attend live?" ? (
                  <>
                    We currently do not share webinar recordings publicly. Please register for the
                    next available live session or{' '}
                    <a
                      href={DEMO_WHATSAPP_URL}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-amber underline underline-offset-2"
                    >
                      book a 1-on-1 setup demo
                    </a>{' '}
                    if you want direct help sooner.
                  </>
                ) : (
                  item.a
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
